/**
 * ErrorHeatMap — "Performance Timeline" block on the eval-results page.
 *
 * TOKEN CONTRACT (RESULTS_THEME_CONVENTION.md):
 *   Every colour, radius, and font reference goes through --results-* or
 *   --font-results-* variables.  No raw Tailwind color utilities.  This
 *   component MUST be rendered inside EvalResultsPageShell (i.e. inside
 *   data-results-page-theme="report") for the tokens to resolve.
 *
 * STAT FRAMING:
 *   The old "N errors (X.X%)" label is replaced with a calm quality read:
 *   "AVG FRAME QUALITY · NN%" derived from stats.avgScore (the arithmetic
 *   mean of per-frame scores already computed in the stats memo).  Color
 *   follows the same score-band logic as the heat bar (good/warn/bad).
 *   The word "errors" does not appear in the rendered output.
 */

import { useMemo } from 'react';
import type { CSSProperties } from 'react';
import { cn } from '@/utils/cn';
import type { PhaseScore } from '@/types/api';

interface FrameScore {
  frame_index: number;
  score: number;
  is_error: boolean;
  procrustes_disparity?: number;
  phase_id?: string;
  phase_label?: string;
}

interface ErrorHeatMapProps {
  frameScores: FrameScore[];
  totalFrames: number;
  currentFrame?: number;
  onFrameClick?: (frameIndex: number) => void;
  className?: string;
  // Phase-related props
  phases?: PhaseScore[];
  onPhaseClick?: (phase: PhaseScore) => void;
  showPhaseLabels?: boolean;
}

// ── Score band helpers ──────────────────────────────────────────────────────

type AccentToken = '--results-accent-good' | '--results-accent-warn' | '--results-accent-bad';

function scoreAccentToken(score: number): AccentToken {
  if (score >= 80) return '--results-accent-good';
  if (score >= 60) return '--results-accent-warn';
  return '--results-accent-bad';
}

// ── ErrorHeatMap ────────────────────────────────────────────────────────────

function ErrorHeatMap({
  frameScores,
  totalFrames,
  currentFrame = 0,
  onFrameClick,
  className,
  phases,
  onPhaseClick,
  showPhaseLabels = true,
}: ErrorHeatMapProps) {
  // Create a map for quick lookup of frame scores
  const scoreMap = useMemo(() => {
    const map = new Map<number, FrameScore>();
    frameScores.forEach((fs) => map.set(fs.frame_index, fs));
    return map;
  }, [frameScores]);

  // Calculate bar width based on total frames
  const barWidth = useMemo(() => {
    if (totalFrames <= 100) return '100%';
    if (totalFrames <= 300) return '33.33%';
    if (totalFrames <= 600) return '16.67%';
    return '10%';
  }, [totalFrames]);

  // Get inline style color for a score value — uses report-theme tokens.
  const getScoreStyle = (score: number, isError: boolean): CSSProperties => {
    const token = isError || score < 60
      ? '--results-accent-bad'
      : score < 80
      ? '--results-accent-warn'
      : '--results-accent-good';
    return { background: `var(${token}, ${token === '--results-accent-bad' ? '#F47B7B' : token === '--results-accent-warn' ? '#F5B731' : '#3DDC97'})` };
  };

  // Group frames into buckets for better visualization with many frames
  const buckets = useMemo(() => {
    const bucketCount = Math.min(totalFrames, 200);
    const framesPerBucket = Math.ceil(totalFrames / bucketCount);
    const result: Array<{
      startFrame: number;
      endFrame: number;
      avgScore: number;
      isError: boolean;
      phaseId?: string;
    }> = [];

    for (let i = 0; i < bucketCount; i++) {
      const start = i * framesPerBucket;
      const end = Math.min(start + framesPerBucket, totalFrames);

      let totalScore = 0;
      let count = 0;
      let hasError = false;
      let phaseId: string | undefined;

      for (let frame = start; frame < end; frame++) {
        const frameScore = scoreMap.get(frame);
        if (frameScore) {
          totalScore += frameScore.score;
          count++;
          if (frameScore.is_error) {
            hasError = true;
          }
          // Use the phase_id from the first frame with phase info
          if (!phaseId && frameScore.phase_id) {
            phaseId = frameScore.phase_id;
          }
        }
      }

      result.push({
        startFrame: start,
        endFrame: end,
        avgScore: count > 0 ? totalScore / count : 100,
        isError: hasError,
        phaseId,
      });
    }

    return result;
  }, [totalFrames, scoreMap]);

  // Calculate phase regions based on phases prop
  const phaseRegions = useMemo(() => {
    if (phases && phases.length > 0) {
      // Use actual phases
      return phases.map((phase) => {
        const startFrame = phase.start_frame ?? 0;
        const endFrame = phase.end_frame ?? totalFrames;
        const widthPercent = ((endFrame - startFrame) / totalFrames) * 100;
        const leftPercent = (startFrame / totalFrames) * 100;

        return {
          phase,
          startFrame,
          endFrame,
          widthPercent,
          leftPercent,
          isFallback: false,
        };
      });
    } else if (showPhaseLabels) {
      // Generate fallback phases when no phase data is available
      const fallbackPhases = [
        { name: 'Early', start: 0, end: Math.floor(totalFrames / 3) },
        { name: 'Middle', start: Math.floor(totalFrames / 3), end: Math.floor(2 * totalFrames / 3) },
        { name: 'Late', start: Math.floor(2 * totalFrames / 3), end: totalFrames }
      ];

      return fallbackPhases.map((fallbackPhase, index) => {
        const startFrame = fallbackPhase.start;
        const endFrame = fallbackPhase.end;
        const widthPercent = ((endFrame - startFrame) / totalFrames) * 100;
        const leftPercent = (startFrame / totalFrames) * 100;

        // Create a phase-like object for fallback
        const phase: PhaseScore = {
          phase_id: `fallback-${index}`,
          phase_name: fallbackPhase.name,
          start_frame: startFrame,
          end_frame: endFrame,
          score: 0, // No score for fallback phases
        };

        return {
          phase,
          startFrame,
          endFrame,
          widthPercent,
          leftPercent,
          isFallback: true,
        };
      });
    }

    return [];
  }, [phases, totalFrames, showPhaseLabels]);

  const handlePhaseClick = (phase: PhaseScore) => {
    if (onPhaseClick) {
      onPhaseClick(phase);
    } else if (onFrameClick && phase.start_frame !== undefined) {
      // Fallback to frame click if no phase click handler
      onFrameClick(phase.start_frame);
    }
  };

  const getCurrentBucketIndex = useMemo(() => {
    return buckets.findIndex(
      (bucket) => currentFrame >= bucket.startFrame && currentFrame < bucket.endFrame
    );
  }, [buckets, currentFrame]);

  // Find current phase based on current frame
  const currentPhase = useMemo(() => {
    if (!phases) return null;
    return phases.find(
      (p) =>
        p.start_frame !== undefined &&
        p.end_frame !== undefined &&
        currentFrame >= p.start_frame &&
        currentFrame <= p.end_frame
    );
  }, [phases, currentFrame]);

  // Calculate stats
  const stats = useMemo(() => {
    const errorFrames = frameScores.filter((fs) => fs.is_error);
    const avgScore =
      frameScores.length > 0
        ? frameScores.reduce((sum, fs) => sum + fs.score, 0) / frameScores.length
        : 100;

    return {
      errorCount: errorFrames.length,
      errorPercentage: totalFrames > 0 ? (errorFrames.length / totalFrames) * 100 : 0,
      avgScore,
    };
  }, [frameScores, totalFrames]);

  // Score band accent token for the avg-quality stat display
  const avgQualityToken = scoreAccentToken(stats.avgScore);

  return (
    <div className={cn('space-y-3', className)} data-testid="error-heatmap">
      {/* Stats row: legend left, avg frame quality right */}
      <div className="flex items-center justify-between text-sm">
        {/* Legend — report-theme accent dots + mono micro-labels */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded"
              style={{ background: 'var(--results-accent-good, #3DDC97)' }}
            />
            <span
              style={{
                fontFamily: 'var(--font-results-mono)',
                fontSize: '10px',
                fontWeight: 600,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'var(--results-text-dim)',
              }}
            >
              Good (&gt;80)
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded"
              style={{ background: 'var(--results-accent-warn, #F5B731)' }}
            />
            <span
              style={{
                fontFamily: 'var(--font-results-mono)',
                fontSize: '10px',
                fontWeight: 600,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'var(--results-text-dim)',
              }}
            >
              Fair (60-80)
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded"
              style={{ background: 'var(--results-accent-bad, #F47B7B)' }}
            />
            <span
              style={{
                fontFamily: 'var(--font-results-mono)',
                fontSize: '10px',
                fontWeight: 600,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'var(--results-text-dim)',
              }}
            >
              Needs work (&lt;60)
            </span>
          </div>
        </div>

        {/* Right-aligned: avg frame quality — calm coaching language, no "errors" */}
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: '6px',
          }}
        >
          <span
            style={{
              fontFamily: 'var(--font-results-mono)',
              fontSize: '10px',
              fontWeight: 600,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: 'var(--results-text-faint)',
            }}
          >
            AVG FRAME QUALITY
          </span>
          <span
            style={{
              fontFamily: 'var(--font-results-mono)',
              fontSize: '13px',
              fontWeight: 700,
              letterSpacing: '0.02em',
              color: `var(${avgQualityToken}, ${avgQualityToken === '--results-accent-good' ? '#3DDC97' : avgQualityToken === '--results-accent-warn' ? '#F5B731' : '#F47B7B'})`,
            }}
          >
            {Math.round(stats.avgScore)}%
          </span>
        </div>
      </div>

      {/* Heat map — phase labels and timeline bar share the same padded container
          so percentage-based widths map to identical pixel ranges for both */}
      <div
        style={{
          background: 'var(--results-surface-raised-1, #111620)',
          border: '1px solid var(--results-surface-line, #222D3F)',
          borderRadius: 'var(--results-radius-card, 14px)',
          padding: '12px',
        }}
      >
        {/* Phase Labels (above timeline, inside the same container) */}
        {showPhaseLabels && phaseRegions.length > 0 && (
          <div className="mb-2" data-testid="phase-labels">
            {/* Fallback indicator text */}
            {phaseRegions.some(r => r.isFallback) && (
              <div className="text-center mb-1">
                <span
                  style={{
                    fontSize: '11px',
                    fontStyle: 'italic',
                    color: 'var(--results-text-faint, #4E5C72)',
                    fontFamily: 'var(--font-results-body)',
                  }}
                >
                  (Phase boundaries estimated)
                </span>
              </div>
            )}
            {/* Phase blocks laid out as flex children, each sized by duration proportion */}
            <div className="flex h-10 gap-px">
              {phaseRegions.map((region, index) => {
                const isCurrentPhase = currentPhase?.phase_id === region.phase.phase_id;

                return (
                  <div
                    key={region.phase.phase_id || index}
                    className={cn(
                      'flex items-center justify-center cursor-pointer overflow-hidden',
                      'transition-all',
                      region.isFallback && 'italic'
                    )}
                    style={{
                      width: `${region.widthPercent}%`,
                      background: 'var(--results-surface-raised-2, #181E2B)',
                      border: isCurrentPhase
                        ? '2px solid var(--results-accent-report, #D4A843)'
                        : '1px solid var(--results-surface-line, #222D3F)',
                      borderRadius: 'var(--results-radius-sm, 8px)',
                      outline: isCurrentPhase
                        ? '1px solid var(--results-accent-report, #D4A843)'
                        : 'none',
                      outlineOffset: '2px',
                    }}
                    onClick={() => handlePhaseClick(region.phase)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handlePhaseClick(region.phase);
                      }
                    }}
                    title={`${region.phase.phase_name || 'Phase'} (frames ${region.startFrame}-${region.endFrame})${
                      region.isFallback ? ' - Estimated' : ''
                    }`}
                    aria-label={`Jump to ${region.phase.phase_name || 'phase'}${
                      region.isFallback ? ' (estimated)' : ''
                    }`}
                  >
                    <span
                      className="truncate px-2"
                      style={{
                        fontFamily: 'var(--font-results-mono)',
                        fontSize: '10px',
                        fontWeight: 600,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        color: region.isFallback
                          ? 'var(--results-text-faint, #4E5C72)'
                          : 'var(--results-text-primary, #EDF0F6)',
                      }}
                    >
                      {region.phase.phase_name || `Phase ${index + 1}`}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="relative">
          {/* Timeline bars - thin heat map stripe (not clickable) */}
          <div
            className="flex items-stretch h-3 gap-px rounded overflow-hidden"
            data-testid="heatmap-timeline"
            style={{ background: 'var(--results-surface-raised-2, #181E2B)' }}
          >
            {buckets.map((bucket, index) => {
              const isCurrentBucket = index === getCurrentBucketIndex;
              const colorStyle = getScoreStyle(bucket.avgScore, bucket.isError);

              return (
                <div
                  key={index}
                  data-testid={`frame-bar-${index}`}
                  className="relative flex-1 transition-all"
                  style={{
                    ...colorStyle,
                    minWidth: barWidth,
                    ...(isCurrentBucket
                      ? {
                          outline: '2px solid var(--results-accent-report, #D4A843)',
                          outlineOffset: '-2px',
                        }
                      : {}),
                  }}
                  title={`Frames ${bucket.startFrame}-${bucket.endFrame - 1}: ${bucket.avgScore.toFixed(1)}%${
                    bucket.isError ? ' (Error)' : ''
                  }`}
                />
              );
            })}
          </div>

          {/* Phase boundary lines (overlay) */}
          {phaseRegions.length > 0 && (
            <div className="absolute inset-0 pointer-events-none">
              {phaseRegions.slice(1).map((region, index) => (
                <div
                  key={`boundary-${index}`}
                  className="absolute top-0 bottom-0 w-1.5 opacity-70"
                  style={{
                    left: `${region.leftPercent}%`,
                    background: 'var(--results-text-primary, #EDF0F6)',
                  }}
                  title={`Phase boundary: ${region.phase.phase_name || `Phase ${index + 2}`}`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Frame markers */}
        <div
          className="flex justify-between mt-2 px-1"
          style={{
            fontFamily: 'var(--font-results-mono)',
            fontSize: '10px',
            color: 'var(--results-text-faint, #4E5C72)',
          }}
        >
          <span>0</span>
          <span>{Math.floor(totalFrames / 2)}</span>
          <span>{totalFrames}</span>
        </div>
      </div>

      {/* Current phase indicator */}
      {currentPhase && (
        <div
          className="text-sm text-center"
          style={{ color: 'var(--results-text-dim, #8A97AD)' }}
        >
          Current Phase:{' '}
          <span
            style={{
              fontFamily: 'var(--font-results-mono)',
              fontWeight: 600,
              color: 'var(--results-accent-report, #D4A843)',
            }}
          >
            {currentPhase.phase_name || 'Unknown'}
          </span>
        </div>
      )}

      {/* Average frame score — kept for backward compat (test suite asserts on it) */}
      <div
        className="text-sm text-center"
        style={{ color: 'var(--results-text-dim, #8A97AD)' }}
      >
        Avg Frame Score:{' '}
        <span
          style={{
            fontFamily: 'var(--font-results-mono)',
            fontWeight: 600,
            color: 'var(--results-text-primary, #EDF0F6)',
          }}
        >
          {stats.avgScore.toFixed(1)}%
        </span>
      </div>
    </div>
  );
}

export default ErrorHeatMap;
